import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { Sparkles, RefreshCw, Target, Lightbulb, Users, Star, Zap } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConceptStep = ({ project, setProject, saveProject, goToStep }) => {
  const { t, language } = useLanguage();
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [concept, setConcept] = useState(null);

  useEffect(() => {
    if (project?.concept) {
      setConcept(project.concept);
      setHasGenerated(true);
    }
  }, []);

  const generateConcept = async () => {
    if (!project?.selected_idea) {
      toast.error(t('common_error'));
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(`${API}/generate/concept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          problem_statement: project.problem_statement,
          selected_idea: project.selected_idea,
          concept: project.concept || null,
          language,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setConcept(data);
        setHasGenerated(true);
        
        await saveProject({ 
          concept: data,
          current_step: 3
        }, false);
        
        setProject(prev => ({ ...prev, concept: data }));
        toast.success(t('common_success'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common_error'));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(t('common_error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleContinue = async () => {
    await saveProject({ concept, current_step: 3 });
    goToStep(4);
  };

  return (
    <div className="h-full flex flex-col overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-4 sm:px-8 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900">
              {t('concept_title')}
            </h2>
            <p className="text-stone-600">
              {t('concept_description')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={generateConcept}
              disabled={isGenerating}
              variant={hasGenerated ? "outline" : "default"}
              className={hasGenerated ? "btn-secondary" : "btn-primary"}
              data-testid="generate-concept-btn"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {t('common_generating')}
                </>
              ) : hasGenerated ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('concept_regenerate')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('concept_generate')}
                </>
              )}
            </Button>
            {hasGenerated && (
              <Button
                onClick={handleContinue}
                className="btn-primary"
                data-testid="continue-to-prototype-btn"
              >
                {t('concept_continue')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 sm:p-8">
        <div className="max-w-4xl mx-auto">
          {!hasGenerated ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-6">
                <Zap className="w-10 h-10 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-stone-900 mb-2">
                {t('concept_generate')}
              </h3>
              <p className="text-stone-600 mb-6 max-w-md mx-auto">
                {t('concept_description')}
              </p>
              <Button
                onClick={generateConcept}
                disabled={isGenerating}
                className="btn-primary"
                data-testid="generate-concept-empty-btn"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('common_generating')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('concept_generate')}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Main Concept Card */}
              <Card className="bg-gradient-to-br from-primary to-primary-hover text-white p-8 rounded-2xl shadow-floating" data-testid="concept-card">
                <div className="mb-4">
                  <span className="text-white/70 text-sm uppercase tracking-wide">{t('concept_name')}</span>
                </div>
                <h1 className="font-heading text-4xl font-semibold mb-2">
                  {concept?.name}
                </h1>
                <p className="text-xl text-white/90 font-medium mb-4">
                  {concept?.tagline}
                </p>
                <p className="text-white/80 leading-relaxed">
                  {concept?.description}
                </p>
              </Card>

              {/* Details Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Problem */}
                <Card className="p-6 border-stone-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <Target className="w-5 h-5 text-red-600" />
                    </div>
                    <h3 className="font-heading font-semibold text-stone-900">
                      {t('concept_problem_solved')}
                    </h3>
                  </div>
                  <p className="text-stone-600 leading-relaxed">
                    {concept?.problem_solved}
                  </p>
                </Card>

                {/* Solution */}
                <Card className="p-6 border-stone-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Lightbulb className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="font-heading font-semibold text-stone-900">
                      {t('concept_solution')}
                    </h3>
                  </div>
                  <p className="text-stone-600 leading-relaxed">
                    {concept?.solution}
                  </p>
                </Card>

                {/* Differentiation */}
                <Card className="p-6 border-stone-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Star className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="font-heading font-semibold text-stone-900">
                      {t('concept_differentiation')}
                    </h3>
                  </div>
                  <p className="text-stone-600 leading-relaxed">
                    {concept?.differentiation}
                  </p>
                </Card>

                {/* Target Audience */}
                <Card className="p-6 border-stone-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="font-heading font-semibold text-stone-900">
                      {t('concept_audience')}
                    </h3>
                  </div>
                  <p className="text-stone-600 leading-relaxed">
                    {concept?.target_audience}
                  </p>
                </Card>
              </div>

              {/* Key Benefits */}
              <Card className="p-6 border-stone-200">
                <h3 className="font-heading font-semibold text-stone-900 mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  {t('concept_benefits')}
                </h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  {concept?.key_benefits?.map((benefit, index) => (
                    <div
                      key={index}
                      className="bg-primary-light/50 p-4 rounded-xl text-stone-700"
                    >
                      <span className="text-primary font-semibold mr-2">{index + 1}.</span>
                      {benefit}
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConceptStep;
